/* ===========================================================
   키즈다 뉴스 — 배너 API (searchWebBannersWithFiles)
   - "키즈다 뉴스" 섹션 좌측 헤드라인 배너: bannerType HEADLINE_BANNER
     · 노출 기간(startedDate ≤ 오늘 ≤ expiredDate), isUsed: Y, ALL
     · contentsType URL → files[0].s3key 이미지 + contentsRef 외부 링크
     · contentsType NEWS → contentsRef(뉴스 seq) 상세 조회 후 표시
     · 없으면 호출부에서 헤드라인 뉴스로 폴백
   - 랜딩 배너(우측 세로 / 상단 가로): bannerType LANDING_BANNER
   =========================================================== */
import graphqlClient from "../../utils/graphql_client_util";
import { s3KeyToImageUrl, getNewsDetail } from "./news_api";

/* 배너 콘텐츠 타입 코드 매핑 (앱 constants.dart CODE.BANNER_CONTENTS 이식)
   1051 → NEWS(기사 상세로 이동), 1052 → URL(외부 링크) */
const BANNER_CONTENTS = {
  1051: "NEWS",
  1052: "URL",
};

/** contentsType → 'NEWS' | 'URL' 로 정규화
 *  - 우선순위: contentsTypeDesc('NEWS'|'URL') → 코드값(1051/1052) → contentsTypeName */
function resolveContentsType(desc, raw, name) {
  // contentsTypeDesc 가 'NEWS' | 'URL' 로 직접 내려옴 — 뉴스/일반 배너 구분의 1차 기준
  const d = String(desc || "").toUpperCase();
  if (d.includes("NEWS")) return "NEWS";
  if (d.includes("URL")) return "URL";
  // 폴백: 코드값(1051/1052) 또는 name
  if (raw == null) return name || "";
  const key = String(raw).replace(/[^0-9]/g, "");
  if (key && BANNER_CONTENTS[key]) return BANNER_CONTENTS[key];
  const up = String(name || raw).toUpperCase();
  if (up.includes("NEWS")) return "NEWS";
  if (up.includes("URL")) return "URL";
  return up;
}

/* ── 랜딩 배너 (searchWebBannersWithFiles) ──
   키즈다 뉴스 우측 세로 배너 + 카테고리/뉴스 섹션 사이 가로 배너용.
   files[].type: 1022 → 가로 이미지, 1023 → 세로 이미지 */
const FILE_TYPE_HORIZONTAL = "1022";
const FILE_TYPE_VERTICAL = "1023";

const WEB_BANNER_LIST_QUERY = `
  query SearchWebBannersWithFiles($input: BannerSearchInput!) {
    searchWebBannersWithFiles(input: $input) {
      totalPages
      totalElements
      success
      size
      page
      error {
        code
        errorType
        message
      }
      data {
        bannerTypeName
        bannerTypeDesc
        contentsTypeName
        contentsTypeDesc
        description
        userTypeName
        userTypeDesc
        contentsRef
        startedDate
        expiredDate
        regName
        regDate
        modName
        modDate
        userType
        type
        title
        isUsed
        files {
          s3key
          type
        }
        contentsType
        bannerSeq
      }
    }
  }
`;

/** 랜딩 배너 item → { id, title, contentsType, contentsRef, horizontalImage, verticalImage } */
function toLandingBanner(item) {
  const files = Array.isArray(item.files) ? item.files : [];
  const horizontal = files.find((f) => String(f?.type) === FILE_TYPE_HORIZONTAL);
  const vertical = files.find((f) => String(f?.type) === FILE_TYPE_VERTICAL);
  return {
    id: String(item.bannerSeq),
    title: item.title || "",
    description: item.description || "",
    contentsType: resolveContentsType(item.contentsTypeDesc, item.contentsType, item.contentsTypeName),
    contentsRef: item.contentsRef || "",
    horizontalImage: s3KeyToImageUrl(horizontal?.s3key),
    verticalImage: s3KeyToImageUrl(vertical?.s3key, { width: 320, fit: "cover" }),
  };
}

/**
 * 랜딩 배너 목록 조회 (LANDING_BANNER / 사용중 / 전체 사용자)
 * @returns {Promise<Array>} 배너 배열 (실패/없음 → [])
 */
export const getLandingBanners = async () => {
  try {
    // 데이터의 날짜는 타임존 없는 로컬 일시(예: "2026-06-15T01:08") 형식이므로
    // 시각까지 비교해야 "오늘 새벽에 만료된 배너"도 정확히 걸러낸다.
    const now = new Date();
    const p = (n) => String(n).padStart(2, "0");
    const nowLocal = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}T${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
    const input = {
      bannerType: "LANDING_BANNER",
      isUsed: "Y",
      bannerUserType: "ALL",
      startedDate: { le: nowLocal },
      expiredDate: { ge: nowLocal },
    };

    const data = await graphqlClient.query(
      WEB_BANNER_LIST_QUERY,
      { input },
      { useAuth: false }
    );

    const res = data?.searchWebBannersWithFiles;
    if (res?.success && Array.isArray(res.data) && res.data.length) {
      // 가로/세로 중 이미지가 하나라도 있는 배너만 사용
      return res.data
        .map(toLandingBanner)
        .filter((b) => b.horizontalImage || b.verticalImage);
    }
    return [];
  } catch (err) {
    console.error("getLandingBanners error:", err);
    return [];
  }
};

/**
/** 헤드라인 배너 item 1건 → 정규화 배너 객체 (이미지 없으면 null)
 *  - URL  : files[0].s3key 이미지 + contentsRef(외부 링크, 새 창)
 *  - NEWS : contentsRef = 뉴스 seq → 뉴스 상세(getNewsDetail)의 정규화 기사 데이터를
 *           그대로 사용(카테고리/태그/요약/이미지/조회수 등) + AD 표기 */
async function toHeadlineBanner(item) {
  if (!item) return null;
  // 'NEWS' | 'URL' 로 정규화 (contentsTypeDesc 우선, 폴백 코드값 1051/1052)
  const contentsType = resolveContentsType(item.contentsTypeDesc, item.contentsType, item.contentsTypeName);

  if (contentsType === "NEWS") {
    // NEWS 배너는 "배너가 아닐 때의 뉴스"와 동일하게 보여야 하므로 기사 데이터를 그대로 넘긴다.
    // (id = 기사 seq → 클릭 시 해당 기사 상세로 이동, 좌상단 AD 뱃지는 UI에서 유지)
    const article = await getNewsDetail(item.contentsRef);
    if (!article || !article.image) return null;
    return {
      ...article,
      bannerSeq: String(item.bannerSeq),
      description: article.summary || item.description || "",
      contentsType: "NEWS",
      contentsRef: String(item.contentsRef || ""),
      isAd: true,
    };
  }

  // URL: files[0].s3key 이미지 + contentsRef 외부 링크(새 창)
  const image = s3KeyToImageUrl(item.files?.[0]?.s3key);
  if (!image) return null;
  return {
    id: String(item.bannerSeq),
    bannerSeq: String(item.bannerSeq),
    title: item.title || "",
    description: item.description || "",
    image,
    contentsType: "URL",
    contentsRef: item.contentsRef || "",
  };
}

/**
 * "키즈다 뉴스" 섹션 좌측 헤드라인 배너 목록 조회
 *  - searchWebBannersWithFiles(bannerType: HEADLINE_BANNER) 사용
 *  - 노출 기간(startedDate ≤ 오늘 ≤ expiredDate), isUsed: Y, 전체 사용자(ALL)
 *  - 응답 data 배열을 모두 정규화 → 호출부에서 1개면 단일, 2개 이상이면 슬라이드 렌더
 *  - 이미지 없는 항목은 제외. 없으면 [] 반환(호출부에서 헤드라인 뉴스로 폴백)
 * @returns {Promise<Array>}  배너 배열 (실패/없음 → [])
 */
export const getNewsTopBanner = async () => {
  try {
    // 날짜는 현재 로컬 일시(yyyy-MM-DDTHH:mm:ss) — 시각까지 비교
    const now = new Date();
    const p = (n) => String(n).padStart(2, "0");
    const nowLocal = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}T${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;

    const input = {
      bannerType: "HEADLINE_BANNER",
      bannerUserType: "ALL",
      isUsed: "Y",
      startedDate: { le: nowLocal },
      expiredDate: { ge: nowLocal },
    };

    const data = await graphqlClient.query(
      WEB_BANNER_LIST_QUERY,
      { input },
      { useAuth: false }
    );

    const res = data?.searchWebBannersWithFiles;
    if (!(res?.success && Array.isArray(res.data) && res.data.length)) return [];

    // 응답 순서를 유지하며 모든 배너를 정규화 (NEWS는 상세 조회가 있어 Promise.all)
    const banners = await Promise.all(res.data.map(toHeadlineBanner));
    return banners.filter(Boolean);
  } catch (err) {
    console.error("getNewsTopBanner error:", err);
    return [];
  }
};
